export interface TermClauseLabel {
  label?: string;
  type?: "hashtags" | "mentions" | "entities";
  excluded?: boolean;
}

export interface QueryClauseLabel {
  label?: string;
  type?: "query";
  excluded?: boolean;
}

export interface DocIdsClauseLabel {
  label?: string;
  type?: "docIds";
  excluded?: boolean;
}
interface RangeClauseLabel<T extends string> {
  type: T;
  gt: number;
  lt: number;
  lowerBound?: boolean;
  upperBound?: boolean;
  excluded?: boolean;
}

export type ReviewClauseLabel = RangeClauseLabel<"review">;
export type AgeClauseLabel = RangeClauseLabel<"age">;
export type FollowersClauseLabel = RangeClauseLabel<"followers">;

export type ClauseLabel =
  | TermClauseLabel
  | ReviewClauseLabel
  | AgeClauseLabel
  | FollowersClauseLabel
  | QueryClauseLabel
  | DocIdsClauseLabel;

export interface ClauseLabels {
  id: string;
  labels: ClauseLabel[];
}

export function isTermClauseLabel(
  label: ClauseLabel
): label is TermClauseLabel {
  return (
    !isReviewClauseLabel(label) &&
    !isAgeClauseLabel(label) &&
    !isFollowersClauseLabel(label) &&
    !isQueryClauseLabel(label)
  );
}

export function isReviewClauseLabel(
  label: ClauseLabel
): label is ReviewClauseLabel {
  return label.type === "review";
}

export function isAgeClauseLabel(label: ClauseLabel): label is AgeClauseLabel {
  return label.type === "age";
}

export function isFollowersClauseLabel(
  label: ClauseLabel
): label is FollowersClauseLabel {
  return label.type === "followers";
}

export function isQueryClauseLabel(
  label: ClauseLabel
): label is QueryClauseLabel {
  return label.type === "query";
}

// LABEL CHECKS
// Range
export const doesRangeClauseLabelHasUpperbound = (
  label: RangeClauseLabel<any>
): boolean => label.upperBound;

export const doesRangeClauseLabelHasLowerbound = (
  label: RangeClauseLabel<any>
): boolean => label.lowerBound;

export const isRangeClauseLabelUpperValueZero = (
  label: RangeClauseLabel<any>
): boolean => label.gt === 0;

export const isRangeClauseLabelLowerValueZero = (
  label: RangeClauseLabel<any>
): boolean => label.lt === 0;

export const isRangeClauseLabelRangeZero = (
  label: RangeClauseLabel<any>
): boolean => {
  return (
    isRangeClauseLabelUpperValueZero(label) &&
    isRangeClauseLabelLowerValueZero(label)
  );
};

export const doesRangeClauseLabelHasLowerValue = (
  label: RangeClauseLabel<any>
): boolean => {
  return !!label.gt;
};

export const doesRangeClauseLabelHasUpperValue = (
  label: RangeClauseLabel<any>
): boolean => {
  return !!label.lt;
};

export const isRangeClauseLabelRangeEqual = (
  label: RangeClauseLabel<any>
): boolean => {
  return label.gt === label.lt;
};
